import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Wrench } from "lucide-react";
import useSeo from "../../hooks/useSeo";
import useEnforceLightMode from "../../hooks/useEnforceLightMode";
import { TOOLS, toolPath, type Tool } from "../../seo/tools";
import { toolSeo } from "./toolUi";

const updatedLabel = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

/** Shared shell for a /tools page: the widget up top, then the explainer, FAQ and CTA. */
export default function ToolLayout({ tool, children, cta }: { tool: Tool; children: ReactNode; cta?: { title: string; body: string; label?: string } }) {
  useEnforceLightMode();
  useSeo(toolSeo(tool));
  const related = TOOLS.filter((t) => t.slug !== tool.slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-16">
        <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-violet-700">Home</Link>
          <span className="mx-2">›</span>
          <Link to="/tools" className="hover:text-violet-700">Free tools</Link>
          <span className="mx-2">›</span>
          <span>{tool.navLabel}</span>
        </nav>

        <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">{tool.h1}</h1>
        <p className="mt-4 text-lg leading-relaxed text-slate-700">{tool.intro}</p>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-6" aria-label={tool.navLabel}>
          {children}
        </section>

        {tool.sections.map((section) => (
          <section key={section.heading} className="mt-10">
            <h2 className="text-2xl font-bold">{section.heading}</h2>
            {section.paragraphs?.map((p) => (
              <p key={p} className="mt-4 leading-relaxed text-slate-700">{p}</p>
            ))}
            {section.bullets && (
              <ul className="mt-4 space-y-2 list-disc pl-6 text-slate-700">
                {section.bullets.map((b) => (
                  <li key={b} className="leading-relaxed">{b}</li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {tool.guidePath && (
          <p className="mt-8">
            <Link to={tool.guidePath} className="inline-flex items-center gap-2 font-semibold text-violet-700 hover:text-violet-800">
              <BookOpen className="w-4 h-4" /> Read the full guide
            </Link>
          </p>
        )}

        <section className="mt-14">
          <h2 className="text-2xl font-bold">Frequently asked questions</h2>
          <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
            {tool.faq.map((f) => (
              <div key={f.q} className="py-5">
                <h3 className="font-semibold">{f.q}</h3>
                <p className="mt-2 leading-relaxed text-slate-700">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-2xl bg-[#3A075C] p-6 sm:p-8 text-white">
          <h2 className="text-2xl font-bold">{cta?.title ?? "Find the markdowns before they're gone"}</h2>
          <p className="mt-2 text-violet-100">
            {cta?.body ?? "DealHawk tracks clearance and price drops at the stores near you and alerts you when prices fall."}
          </p>
          <Link to="/get-started" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 font-semibold text-[#3A075C]">
            {cta?.label ?? "Start free"} <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        <section className="mt-14">
          <h2 className="text-xl font-bold">More free tools</h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-3">
            {related.map((t) => (
              <li key={t.slug}>
                <Link to={toolPath(t.slug)} className="flex h-full items-start gap-3 rounded-xl border border-slate-200 p-4 hover:border-violet-300">
                  <Wrench className="w-5 h-5 mt-0.5 text-violet-700 flex-shrink-0" />
                  <span className="font-semibold">{t.navLabel}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <p className="mt-10 text-xs text-slate-400">Updated {updatedLabel(tool.updated)}. DealHawk isn't affiliated with the retailers mentioned.</p>
      </article>
    </div>
  );
}
